import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import React, { forwardRef } from 'react';
import CheckContained from 'aquilla/icons/misc/CheckContained';
import pushRoute from '@utils/pushRoute';
import { useIsMobile } from '@stores/AppCommonData';
import { getRegularDate } from '@utils/dates';
import AttemptMockTest from 'aquilla/icons/unacademyStore/AttemptMockTest';
import BatchAccessEnded from 'aquilla/icons/unacademyStore/BatchAccessEnded';
import { emptyObject } from '@constants/empty';
import useTestSeriesInfo from './useTestSeriesInfo';

const Container = styled.div`
  margin-top: ${Spacings.SPACING_14B};
  ${mediaQuery.uptoMobile} {
    margin-top: 0;
  }
`;

const Title = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_6B};
`;

const StyledButton = styled(Button)`
  width: 222px;
  height: ${Spacings.SPACING_12B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const List = styled.ul`
  padding-left: 0;
  list-style: none;
  margin-bottom: ${Spacings.SPACING_6B};
`;

const ListItem = styled.li`
  font-size: ${Fonts.P1};
  color: var(--color-text-primary);
  line-height: ${Spacings.SPACING_5B};
  display: flex;
  align-items: center;
  & :not(:last-child) {
    margin-bottom: ${Spacings.SPACING_3B};
  }

  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.ULTRA_SMALL};
  }
`;

const ListItemName = styled.div`
  margin-left: ${Spacings.SPACING_6};
`;

const StyledP2 = styled(Typography)`
  line-height: 18px;
  font-weight: ${Fonts.SEMIBOLD};
  margin-bottom: ${Spacings.SPACING_2};
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 483px;
  justify-content: space-between;
  height: ${Spacings.SPACING_10B};
`;

const Wrapper = styled(FlexRow)`
  height: auto;
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: ${Spacings.SPACING_4B};
    & > :nth-child(2) {
      margin-top: ${Spacings.SPACING_4B};
    }
  }
  &:empty {
    margin: 0;
  }
`;

const IconWrapper = styled.div`
  margin-right: ${Spacings.SPACING_3B};
  height: ${Spacings.SPACING_10B};
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const Schedule = forwardRef((props, ref) => {
  const {
    fromStore,
    expiryTime,
    isSkuAvailable,
    availableSku,
    hasSkuAccess,
    subscription
  } = props;
  const { data } = useTestSeriesInfo();

  const { schedule = {}, frequency } = data;
  const isMobile = useIsMobile();

  const { accessEndDate, displayDuration, duration } = availableSku;

  const { duration: postDuration = '', accessEndDate: postAccessEndDate = '' } =
    subscription ?? emptyObject;

  const getScheduleInfo = () => {
    return schedule.info?.map((item, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <ListItem key={index}>
        <CheckContained width={20} height={20} />
        <ListItemName>{item}</ListItemName>
      </ListItem>
    ));
  };

  const isDurationBased =
    (!accessEndDate && !!duration) || (!postAccessEndDate && !!postDuration);

  const handleOnClick = () => {
    pushRoute(
      fromStore
        ? `/store/test-series/${data.slug}/${data.uid}/full-schedule`
        : `/test-series/${data.slug}/${data.uid}/full-schedule`
    );
  };

  const getAccessEndDate = () => {
    if (hasSkuAccess && expiryTime) {
      return expiryTime;
    }
    if (isSkuAvailable && availableSku.accessEndDate) {
      return availableSku.accessEndDate;
    }
    return null;
  };

  const getTitleText = () => {
    if (isDurationBased && !hasSkuAccess) {
      return 'Access available for';
    }
    if (isDurationBased && hasSkuAccess) {
      return 'Access available till';
    }
    return 'Test series access ends on';
  };

  return (
    <Container ref={ref}>
      {!isMobile && <Title variant="h4">Schedule</Title>}
      <List>{getScheduleInfo()}</List>
      <Wrapper>
        {frequency && (
          <FlexRow>
            <IconWrapper>
              <AttemptMockTest width={40} height={40} />
            </IconWrapper>

            <FlexColumn>
              <StyledP2 variant="p2">Mock tests</StyledP2>
              <Typography variant="p3" color="var(--color-text-secondary)">
                {frequency}
              </Typography>
            </FlexColumn>
          </FlexRow>
        )}
        {((fromStore && getAccessEndDate()) || isDurationBased) && (
          <FlexRow>
            <IconWrapper>
              <BatchAccessEnded width={40} height={40} />
            </IconWrapper>

            <FlexColumn>
              <StyledP2 variant="p2">{getTitleText()}</StyledP2>
              <Typography variant="p3" color="var(--color-text-secondary)">
                {isDurationBased && !hasSkuAccess
                  ? displayDuration
                  : getRegularDate(getAccessEndDate(), false)}
              </Typography>
            </FlexColumn>
          </FlexRow>
        )}
      </Wrapper>

      <StyledButton
        label="See full schedule"
        variant="secondary"
        size="large"
        onClick={handleOnClick}
      />
    </Container>
  );
});

Schedule.displayName = 'Schedule';

export default React.memo(Schedule);
